import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { addDepartment, clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { FaArrowLeft, FaSave } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { applyFieldFilter } from '../../../../shared/utils.js/fieldValidation';

const AddDepartment = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, error, success } = useSelector((state) => state.auth);
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    description_en: '',
    description_ar: ''
  });
  const lastErrorRef = useRef({ message: null, time: 0 });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : applyFieldFilter(name, value);
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name_en || !formData.name_ar || !formData.description_en || !formData.description_ar) {
      toast.error(t('departments.addForm.requiredFields'), { rtl: isRTL });
      return;
    }
    await dispatch(addDepartment(formData));
  };

  React.useEffect(() => {
    if (success) {
      toast.success(success, { 
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      
      setTimeout(() => {
        navigate('/departments');
        dispatch(clearSuccess());
      }, 2000);
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
      toast.error(error, { 
        rtl: isRTL,
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [success, error, navigate, dispatch, isRTL]);

  const handleCancel = () => {
    navigate('/departments');
  };

  return (
    <div style={{ 
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir,
      textAlign
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          backgroundColor: '#1b3350',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid #333'
        }}>
          <div style={{
            padding: '20px',
            borderBottom: '1px solid #333',
            backgroundColor: '#1a1f2e'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h1 style={{ 
                margin: 0, 
                fontSize: '24px', 
                fontWeight: 'bold',
                color: 'white',
                textAlign
              }}>
                {t('departments.addForm.title')}
              </h1>
              <button
                onClick={handleCancel}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse'
                }}
              >
                <FaArrowLeft />
                {t('departments.addForm.back')}
              </button>
            </div>
          </div>

          <div style={{ padding: '30px' }}>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                {/* الاسم بالعربية */}
                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 'bold',
                    color: 'white',
                    fontSize: '14px',
                    textAlign
                  }}>
                    {t('departments.addForm.nameAr')} *
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={formData.name_ar}
                    onChange={handleChange}
                    placeholder={t('departments.addForm.nameArPlaceholder')}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: dir,
                      textAlign
                    }}
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 'bold',
                    color: 'white',
                    fontSize: '14px',
                    textAlign
                  }}>
                    {t('departments.addForm.nameEn')} *
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={formData.name_en}
                    onChange={handleChange}
                    placeholder={t('departments.addForm.nameEnPlaceholder')}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: dir,
                      textAlign
                    }}
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 'bold',
                    color: 'white',
                    fontSize: '14px',
                    textAlign
                  }}>
                    {t('departments.addForm.descriptionAr')} *
                  </label>
                  <textarea
                    name="description_ar"
                    value={formData.description_ar}
                    onChange={handleChange}
                    placeholder={t('departments.addForm.descriptionArPlaceholder')}
                    rows="3"
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      resize: 'vertical',
                      direction: dir,
                      textAlign
                    }}
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 'bold',
                    color: 'white',
                    fontSize: '14px',
                    textAlign
                  }}>
                    {t('departments.addForm.descriptionEn')} *
                  </label>
                  <textarea
                    name="description_en"
                    value={formData.description_en}
                    onChange={handleChange}
                    placeholder={t('departments.addForm.descriptionEnPlaceholder')}
                    rows="3"
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      resize: 'vertical',
                      direction: dir,
                      textAlign
                    }}
                  />
                </div>
              </div>
       

              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '15px' 
              }}>
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  {t('departments.addForm.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    backgroundColor: '#AC2000',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: isLoading ? 0.7 : 1,
                    flexDirection: isRTL ? 'row' : 'row-reverse'
                  }}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      {t('departments.addForm.saving')}
                    </>
                  ) : (
                    <>
                      <FaSave />
                      {t('departments.addForm.save')}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddDepartment;

